import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllPatients, uploadLabReport, getAllLabReports, API_ORIGIN } from '../../api';
import styles from './UploadLabReport.module.css';

const UploadLabReport = () => {
  const [patients, setPatients] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchPatients();
    fetchRecentReports();
  }, []);

  const fetchPatients = async () => {
    try {
      const res = await getAllPatients();
      setPatients(res.data || []);
    } catch (error) {
      showAlert('error', 'Error fetching patients');
    }
  };

  const fetchRecentReports = async () => {
    try {
      const reports = await getAllLabReports();
      setRecentReports(reports.slice(0, 5)); // Show last 5
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPatient || !file) {
      showAlert('error', 'Please select a patient and upload a file');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('patientId', selectedPatient);
      formData.append('file', file);
      formData.append('description', description);
      
      const res = await uploadLabReport(formData);
      if (res && res.message) {
        showAlert('success', res.message);
        setFile(null);
        setDescription('');
        setSelectedPatient('');
        fetchRecentReports();
      } else {
        showAlert('error', 'Failed to upload lab report');
      }
    } catch (error) {
      showAlert('error', 'Error uploading lab report');
    } finally {
      setLoading(false);
    }
  };

  const getFileUrl = (filePath) => {
    if (!filePath) return '';
    const normalized = String(filePath).replace(/\\/g, '/');
    return `${API_ORIGIN}/${normalized.startsWith('/') ? normalized.slice(1) : normalized}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Upload Lab Report</h2>
        <p>Select a patient and upload a lab report file</p>
      </div>

      {alert.show && (
        <div className={`${styles.alert} ${styles[`alert${alert.type.charAt(0).toUpperCase() + alert.type.slice(1)}`]}`}>
          {alert.message}
        </div>
      )}

      <div className={styles.mainLayout}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Select Patient *</label>
            {patients.length === 0 ? (
              <div className={styles.noPatients}>
                No patients found. <button type="button" onClick={() => navigate('/admin/create-user')} className={styles.linkBtn}>Create Patient First</button>
              </div>
            ) : (
              <select
                className={styles.select}
                value={selectedPatient}
                onChange={e => setSelectedPatient(e.target.value)}
                required
              >
                <option value="">-- Select Patient --</option>
                {patients.map(patient => (
                  <option key={patient._id} value={patient._id}>
                    {patient.patientName} ({patient.email || 'No email'})
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Lab Report File *</label>
            <input
              className={styles.input}
              type="file"
              accept="application/pdf,image/*"
              onChange={e => setFile(e.target.files[0])}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Description (optional)</label>
            <input
              className={styles.input}
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Add any notes or description"
            />
          </div>
          <div className={styles.formActions}>
            <button className={`${styles.btn} ${styles.btnPrimary}`} type="submit" disabled={loading || patients.length === 0}>
              {loading ? 'Uploading...' : 'Upload Report'}
            </button>
          </div>
        </form>

        <div className={styles.recentHistory}>
          <h3>Recently Uploaded Reports</h3>
          {recentReports.length === 0 ? (
            <p className={styles.emptyText}>No reports uploaded yet.</p>
          ) : (
            <div className={styles.reportList}>
              {recentReports.map(report => (
                <div key={report._id} className={styles.reportItem}>
                  <div className={styles.reportInfo}>
                    <span className={styles.reportName}>{report.fileName}</span>
                    <span className={styles.reportDate}>{new Date(report.uploadedAt).toLocaleDateString()}</span>
                  </div>
                  <button 
                    className={styles.viewLink} 
                    onClick={() => window.open(getFileUrl(report.filePath), '_blank')}
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadLabReport; 